import React from "react";
import { Link } from "react-router-dom";
import { FormattedMessage, FormattedDate, useIntl } from "react-intl";
import { IonItem, IonLabel, IonList } from "@ionic/react";
import { Settings } from "../../../interfaces";
import { IconCalendarTime, IconArrowsSort } from "@tabler/icons-react";
import styles from "./styles.module.css";
import NavDropdown from "react-bootstrap/NavDropdown";

interface ILayoutProps {
  settings: Settings;
  showCta?: boolean;
  isMobile?: boolean;
}

export type PlanUsageComputed = {
  isPro?: boolean; // is this a Pro plan?

  total: number;
  limit: number;
  remaining: number;
  pct: number; // 0-100 float
  pctRounded: number; // 0-100 int
  resetDate: Date;

  // formatted (locale-aware)
  totalFormatted: string; // "1,516"
  limitFormatted: string; // "40,000"
  remainingFormatted: string; // "38,484"
  totalCompact: string; // "1.5K"
  limitCompact: string; // "40K"
  remainingCompact: string; // "38.5K"
  pctText: string; // "3.8%"

  // UI helpers
  status: "success" | "warning" | "danger";
  statusLabel: string; // "Plenty left", "Near limit", "Limit reached"
  planLabel: string; // e.g., "Plan PRO"
  isPastDue: boolean; // is the payment past due?
  isLimitApproaching: boolean; // is the limit approaching?
  isLimitReached: boolean; // is the limit reached?
};

export function computePlanUsage(
  settings: Settings,
  opts: { locale?: string } = {},
): PlanUsageComputed {
  const {
    totalMessages,
    messageLimit,
    endDate,
    limitApproaching,
    limitReached,
  } = settings.status.plan;
  const { active, pastDue } = settings.status.payment;
  const locale = opts.locale ?? "en-US";

  const isPro = active;

  const isPastDue = pastDue ? true : false;
  const isLimitApproaching = limitApproaching ? true : false; // arbitrary threshold for "approaching limit"
  const isLimitReached = limitReached ? true : false; // no messages left

  const nf = new Intl.NumberFormat(locale);
  const nfCompact = new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1,
  });

  const total = Number(totalMessages) || 0; // used
  const limit = Math.max(0, Number(messageLimit) || 0);
  const remaining = Math.max(0, limit - total);

  // now % means "left"
  const computedPctLeft = limit > 0 ? (remaining / limit) * 100 : 0;
  const pctClamped = Math.min(100, Math.max(0, computedPctLeft));
  const pctRounded = Math.round(pctClamped);

  // statuses are based on what's LEFT
  // success: plenty left (>=30%), warning: getting low (10–30%), danger: very low (<=10%)
  let status: PlanUsageComputed["status"] = "success";
  let statusLabel = "plentyLeft";

  if (isLimitReached) {
    status = "danger";
    statusLabel = "limitReached"; // add i18n key
  } else if (isLimitApproaching) {
    status = "warning";
    statusLabel = "nearLimit"; // add i18n key
  }

  const resetDate = new Date(endDate);

  return {
    isPro,
    total,
    limit,
    remaining,
    pct: pctClamped, // % LEFT
    pctRounded,
    resetDate,
    totalFormatted: nf.format(total),
    limitFormatted: nf.format(limit),
    remainingFormatted: nf.format(remaining),
    totalCompact: nfCompact.format(total),
    limitCompact: nfCompact.format(limit),
    remainingCompact: nfCompact.format(remaining),
    pctText: `${pctClamped.toFixed(1)}%`, // % LEFT
    status,
    statusLabel,
    planLabel: `Plan ${isPro ? "PRO" : "FREE"}`,
    isPastDue,
    isLimitApproaching,
    isLimitReached,
  };
}

export const PieChart = ({
  size = 155,
  stroke = 12,
  radius = size / 2,
  pct = 0,
  innerText = false,
  showPct = true,
  status = "success",
}: {
  size?: number;
  stroke?: number;
  radius?: number;
  pct?: number;
  innerText?: boolean;
  showPct?: boolean;
  status?: "success" | "warning" | "danger";
}) => {
  const normalizedRadius = radius - stroke * 0.5;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (pct / 100) * circumference;
  return (
    <div
      className={styles.pieChartContainer}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        flex: `0 0 ${size}px`,
      }}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className={styles.pieChartSvg}
        aria-hidden="true"
      >
        <circle
          stroke="var(--ion-color-step-100)"
          fill="transparent"
          strokeWidth={stroke}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          className={`${styles.pieChartProgress} ${styles[status]}`}
          fill="transparent"
          strokeWidth={stroke}
          strokeLinecap="butt"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
      </svg>
      <div className={styles.pieCenter} style={{ padding: stroke + 6 }}>
        {showPct ? <div className={styles.pct}>{pct}%</div> : null}
        <div className={styles.subtext}>
          {innerText ? (
            <FormattedMessage
              id="home.planUsageHelper"
              defaultMessage="Messages remaining from your plan"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const PlanUsageNavbar: React.FC<ILayoutProps> = (
  props: ILayoutProps,
) => {
  const { settings, isMobile } = props;
  const intl = useIntl();
  const { limitCompact, remainingCompact, pct, pctRounded, status } =
    computePlanUsage(props.settings);
  const trialEndsAt = settings.status?.payment?.trialEndsAt
    ? new Date(settings.status.payment.trialEndsAt)
    : null;
  const trialEndsAtMs = trialEndsAt?.getTime?.() || 0;
  const isTrialActive =
    Boolean(settings.status?.payment?.isTrial) && trialEndsAtMs > Date.now();
  const trialDaysLeft = isTrialActive
    ? Math.max(
        1,
        Math.ceil((trialEndsAtMs - Date.now()) / (1000 * 60 * 60 * 24)),
      )
    : 0;
  const trialBadgeTitle =
    isTrialActive && trialEndsAt
      ? intl.formatMessage(
          { id: "home.trialEndsOn" },
          {
            date: intl.formatDate(trialEndsAt, {
              day: "numeric",
              month: "short",
              year: "numeric",
            }),
          },
        )
      : "";

  return (
    <>
      <div
        className={
          isMobile ? styles.planUsageNavbarMobile : styles.planUsageNavbar
        }
      >
        <PieChart
          size={isMobile ? 20 : 22}
          stroke={5}
          pct={pct}
          showPct={false}
          status={status}
        />
        {isMobile ? <IconArrowsSort size="18" /> : null}
        {isMobile ? (
          <div className={styles.planUsageNavbarCopy}>
            <div className={styles.navbarTextMobile}>
              {settings.loading ? "..." : remainingCompact} /{" "}
              {settings.loading ? "..." : limitCompact}
            </div>
            {isTrialActive && (
              <span className={styles.trialPill} title={trialBadgeTitle}>
                <FormattedMessage
                  id="home.trialBadge"
                  values={{ days: trialDaysLeft }}
                />
              </span>
            )}
          </div>
        ) : (
          <div className={styles.planUsageNavbarCopy}>
            <FormattedMessage
              id="home.quotaLeft"
              values={{
                remaining: settings.loading ? "..." : remainingCompact,
                total: settings.loading ? "..." : limitCompact,
                pct: pctRounded,
              }}
            />
            {isTrialActive && (
              <span className={styles.trialPill} title={trialBadgeTitle}>
                <FormattedMessage
                  id="home.trialBadge"
                  values={{ days: trialDaysLeft }}
                />
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const PlanUsage: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const {
    limitFormatted,
    remainingFormatted,
    pct,
    pctRounded,
    resetDate,
    statusLabel,
    status,
  } = computePlanUsage(props.settings);

  const { isMobile, showCta } = props;
  const trialEndsAt = props.settings.status?.payment?.trialEndsAt
    ? new Date(props.settings.status.payment.trialEndsAt)
    : null;
  const trialEndsAtMs = trialEndsAt?.getTime?.() || 0;
  const isTrialActive =
    Boolean(props.settings.status?.payment?.isTrial) &&
    trialEndsAtMs > Date.now();
  const trialChargeDate =
    isTrialActive && trialEndsAt ? trialEndsAt : resetDate;

  const size = 130;
  const stroke = 14;

  const pieChart = PieChart({
    size,
    stroke,
    pct: pctRounded,
    status,
    innerText: true,
  });
  // Mobile Progress Bar
  const progressBar = (
    <div className={`progress ${styles.progressUsage}`}>
      <div
        className={`progress-bar ${status}`}
        role="progressbar"
        aria-valuenow={Number(pct.toFixed(0))}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{ width: `${pct}%` }}
      />
    </div>
  );

  return isMobile ? (
    <>
      <div className="view-pane-title mt-4">
        <FormattedMessage id="home.messageLimit" />
      </div>
      <IonList inset={true}>
        <IonItem>
          <IonLabel>
            <div className="flex align-items-center gap-2">
              <IconArrowsSort size="18" />
              <div className="text-nowrap fs-3 color-step-700">
                <FormattedMessage
                  id="home.remainingQuotaOutOf"
                  values={{
                    remaining: remainingFormatted,
                    limit: limitFormatted,
                  }}
                />
              </div>
            </div>
          </IonLabel>
        </IonItem>

        <IonItem>
          <div style={{ width: "100%" }}>{progressBar}</div>
        </IonItem>

        <IonItem>
          <IonLabel>
            <div className="flex align-items-center gap-2">
              <IconCalendarTime size="18" />
              <div className="text-nowrap fs-3  gap-1 color-step-700">
                <FormattedMessage
                  id="home.dateLimit"
                  values={{
                    date: (
                      <FormattedDate
                        value={resetDate}
                        timeZone="UTC"
                        day="numeric"
                        month="short"
                        year="numeric"
                      />
                    ),
                  }}
                />
              </div>
            </div>
          </IonLabel>
        </IonItem>
        {isTrialActive ? (
          <IonItem>
            <IonLabel>
              <div className={styles.trialNotice}>
                <div className={styles.trialNoticeTitle}>
                  <FormattedMessage id="home.trialNoticeTitle" />
                </div>
                <div className={styles.trialNoticeBody}>
                  <FormattedMessage
                    id="home.trialNoticeBody"
                    values={{
                      date: (
                        <FormattedDate
                          value={trialChargeDate}
                          timeZone="UTC"
                          day="numeric"
                          month="short"
                          year="numeric"
                        />
                      ),
                    }}
                  />
                </div>
                <Link to="/settings/payment" className={styles.trialNoticeLink}>
                  <FormattedMessage id="home.trialNoticeCta" />
                </Link>
              </div>
            </IonLabel>
          </IonItem>
        ) : null}
      </IonList>
    </>
  ) : (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h3 className="fs-3 mb-0 color-step-900">
            <FormattedMessage id="home.messageLimit" />
          </h3>
        </div>
        {showCta ? (
          <NavDropdown.Item
            as={Link}
            to="/settings/payment"
            className={`btn btn-visito-primary btn-sm ${styles.subscribeCta}`}
          >
            <FormattedMessage id="nav.paymentCTA" />
          </NavDropdown.Item>
        ) : null}
      </div>

      <div className="mt-4 d-flex align-items-start gap-4">
        {pieChart}

        <div className="d-flex flex-column gap-2">
          <div className="flex align-items-center gap-2">
            <IconArrowsSort size="18" className="color-step-700" />
            <div className="text-nowrap fs-3 color-step-700">
              <FormattedMessage
                id="home.remainingQuotaOutOf"
                values={{
                  remaining: remainingFormatted,
                  limit: limitFormatted,
                }}
              />
            </div>
          </div>
          <div className="flex align-items-center gap-2">
            <IconCalendarTime size="18" className="color-step-700" />
            <div className="text-nowrap fs-3  gap-1 color-step-700">
              <FormattedMessage
                id="home.dateLimit"
                values={{
                  date: (
                    <FormattedDate
                      value={resetDate}
                      timeZone="UTC"
                      day="numeric"
                      month="short"
                      year="numeric"
                    />
                  ),
                }}
              />
            </div>
          </div>

          <div className="mt-2">
            <span className={`badge text-bg-${status}`}>
              <FormattedMessage id={`home.${statusLabel}`} />
            </span>
          </div>
          {isTrialActive ? (
            <div className={styles.trialNotice}>
              <div className={styles.trialNoticeTitle}>
                <FormattedMessage id="home.trialNoticeTitle" />
              </div>
              <div className={styles.trialNoticeBody}>
                <FormattedMessage
                  id="home.trialNoticeBody"
                  values={{
                    date: (
                      <FormattedDate
                        value={trialChargeDate}
                        timeZone="UTC"
                        day="numeric"
                        month="short"
                        year="numeric"
                      />
                    ),
                  }}
                />
              </div>
              <Link to="/settings/payment" className={styles.trialNoticeLink}>
                <FormattedMessage id="home.trialNoticeCta" />
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PlanUsage;
